import type { RuntimeConfig } from '@/runtime-config/RuntimeConfig';

/**
 * Operational tuning knobs for the signal tiles' live detectors — windows,
 * thresholds, severity cutoffs. Sourced from the BFF runtime-config `signals`
 * section (env/YAML overridable, no rebuild); {@link DEFAULT_SIGNAL_TUNING} is
 * the fallback when a field is absent. Must stay in shape-parity with the BFF
 * `SignalsSection` zod schema.
 */
export interface SignalTuning {
    anomalyWindowDays: number;
    anomalyLimit: number;
    decliningMinPriorCalls: number;
    decliningDropRatio: number;
    decliningMediumRatio: number;
    sessionDriftHighTopics: number;
    sessionDriftMediumTopics: number;
    rePromptWindowMinutes: number;
    rePromptHighRestarts: number;
    rePromptMediumRestarts: number;
    activityWindowDays: number;
    activityDailyPerWeek: number;
    activityFourPlusPerWeek: number;
    activityOneToThreePerWeek: number;
    stickinessWindowDays: number;
    spendWindowDays: number;
    licenseRoiMaxRatio: number;
    inactivityDays: number;
    shadowSeatHighTurns: number;
    shadowSeatMediumTurns: number;
}

export const DEFAULT_SIGNAL_TUNING: SignalTuning = {
    anomalyWindowDays: 30,
    anomalyLimit: 3,
    decliningMinPriorCalls: 5,
    decliningDropRatio: 0.5,
    decliningMediumRatio: 0.25,
    sessionDriftHighTopics: 4,
    sessionDriftMediumTopics: 3,
    rePromptWindowMinutes: 60,
    rePromptHighRestarts: 5,
    rePromptMediumRestarts: 2,
    activityWindowDays: 56,
    activityDailyPerWeek: 6,
    activityFourPlusPerWeek: 4,
    activityOneToThreePerWeek: 1,
    stickinessWindowDays: 30,
    spendWindowDays: 30,
    licenseRoiMaxRatio: 3,
    inactivityDays: 30,
    shadowSeatHighTurns: 100,
    shadowSeatMediumTurns: 20,
};

/** Merge the runtime-config `signals` section over the defaults, so a partial or absent section still yields a complete tuning. */
export function resolveSignalTuning(runtimeConfig: RuntimeConfig): SignalTuning {
    return { ...DEFAULT_SIGNAL_TUNING, ...runtimeConfig.signals };
}
